import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Lock, Calendar, MapPin, Heart, Building2, Gift, Ticket, Star, LogOut, ChevronRight, Check, Trophy, Share2, Users, Copy, Award, History, Eye, EyeOff, BellOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Level colors and icons
const LEVEL_CONFIG = {
  bronce: { color: '#CD7F32', label: 'Bronce', icon: Award, min: 0, max: 499 },
  plata: { color: '#C0C0C0', label: 'Plata', icon: Trophy, min: 500, max: 1499 },
  oro: { color: '#FFD700', label: 'Oro', icon: Star, min: 1500, max: Infinity }
};

// Member Registration Form
const RegisterForm = ({ onSuccess, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    first_name: '', last_name: '', birth_date: '', phone: '',
    city: '',
    favorite_genres: [], accept_communications: true
  });
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [cityQuery, setCityQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const filteredCities = cities.filter(city =>
    city.label.toLowerCase().includes(cityQuery.toLowerCase())
  );

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const res = await axios.get(`${API}/genres`);
        setGenres(res.data);
      } catch (err) {
        console.error('Error loading genres:', err);
      }
    };
    fetchGenres();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch("/api/cities"); // ajusta si tu baseURL es distinta
        const data = await res.json();
        setCities(data);
      } catch (error) {
        console.error("Error loading cities:", error);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, []);

  // Check if email already exists
  const checkEmailExists = async () => {
    if (!formData.email) return false;
    setCheckingEmail(true);
    try {
      const res = await axios.post(`${API}/members/check-email`, { email: formData.email });
      return res.data.exists;
    } catch (err) {
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  // Validate step 1 before proceeding
  const handleNextStep = async () => {
    setError('');
    
    if (step === 1) {
      // Check email exists
      const emailExists = await checkEmailExists();
      if (emailExists) {
        setError('Este email ya está registrado. ¿Quieres iniciar sesión?');
        return;
      }
      
      // Check passwords match
      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return;
      }
      
      if (formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }
    }
    
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!recaptchaToken) {
    setError('Por favor, completa el captcha "No soy un robot"');
    return;
  }
  
  setLoading(true);
  setError('');

  try {
    const res = await axios.post(`${API}/members/register`, {
      email: formData.email,
      password: formData.password,
      first_name: formData.first_name,
      last_name: formData.last_name,
      birth_date: formData.birth_date,
      phone: formData.phone,
      address: { street: '', city: formData.city, postal_code: '' },
      favorite_genres: formData.favorite_genres,
      accept_communications: formData.accept_communications,
      recaptcha_token: recaptchaToken
    });

    // ✅ Flujo nuevo: siempre a “revisa tu email”
    toast.success('Te hemos enviado un email para verificar tu cuenta.');
    navigate(`/verificacion-enviada?email=${encodeURIComponent(formData.email)}`);

    // ✅ (Opcional) Si en algún entorno legacy devolviese token + member, lo soportamos:
    // if (res.data?.token && res.data?.member) {
    //   localStorage.setItem('memberToken', res.data.token);
    //   localStorage.setItem('memberData', JSON.stringify(res.data.member));
    //   window.dispatchEvent(new Event('memberUpdate'));
    //   onSuccess(res.data.member);
    // }

  } catch (err) {
    setError(err.response?.data?.detail || 'Error al registrar');
    console.log("Error al registrar:", err);
    if (recaptchaRef.current) recaptchaRef.current.reset();
    setRecaptchaToken('');
  } finally {
    setLoading(false);
  }
};


  const toggleGenre = (genreValue) => {
    setFormData(prev => ({
      ...prev,
      favorite_genres: prev.favorite_genres.includes(genreValue)
        ? prev.favorite_genres.filter(g => g !== genreValue)
        : [...prev.favorite_genres, genreValue]
    }));
  };

  const isValidDate = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    const mindate = new Date("1900/01/01");
    if (date > today || mindate > date) return false;
    return true;
  };

  const isValidPhone = (phone) => {
    if (!phone) return true; // opcional
    const numbers = phone.replace(/\s/g, "");
    if (!/^\d{9}$/.test(numbers)) return false;
    if (!/^[6789]/.test(numbers)) return false;
    return true;
  };

  const handlePhoneChange = (value) => {
    let numbers = value.replace(/\D/g, "");
    numbers = numbers.slice(0, 9);
    let formatted = numbers;
    if (numbers.length > 6) {
      formatted = `${numbers.slice(0,3)} ${numbers.slice(3,6)} ${numbers.slice(6)}`;
    } else if (numbers.length > 3) {
      formatted = `${numbers.slice(0,3)} ${numbers.slice(3)}`;
    }
    setFormData(p => ({ ...p, phone: formatted }));
  };


  const canProceedStep1 = formData.email && formData.password && formData.confirmPassword;
  const canProceedStep2 = formData.first_name && formData.last_name && isValidDate(formData.birth_date);
  const passwordsMatch = formData.password === formData.confirmPassword;

  return (
    <div className="max-w-xl mx-auto">
      {/* Progress Steps - Now 3 steps instead of 4 */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className={`flex items-center ${s < 3 ? 'flex-1' : ''}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= s ? 'bg-[var(--primary)] text-black' : 'bg-white/10 text-white/50'
            }`}>
              {step > s ? <Check size={16} /> : s}
            </div>
            {s < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > s ? 'bg-[var(--primary)]' : 'bg-white/10'}`} />}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-950/50 border border-red-500/30 text-red-400 text-sm">
            {error}
            {error.includes('iniciar sesión') && (
              <button type="button" onClick={onSwitchToLogin} className="ml-2 underline hover:text-red-300">
                Iniciar sesión
              </button>
            )}
          </div>
        )}

        {/* Step 1: Account */}
        {step === 1 && (
          <div className="space-y-4 animate-fadeIn">
            <h3 className="text-xl text-white font-medium flex items-center gap-2">
              <Lock size={20} className="text-[var(--primary)]" />
              Datos de acceso
            </h3>
            <div>
              <label className="text-white/60 text-sm block mb-2">Email *</label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="tu@email.com" className="bg-transparent border-white/20 text-white" required />
            </div>
            <div>
              <label className="text-white/60 text-sm block mb-2">Contraseña *</label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                  placeholder="Mínimo 6 caracteres" className="bg-transparent border-white/20 text-white pr-10" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-white/60 text-sm block mb-2">Confirmar contraseña *</label>
              <div className="relative">
                <Input type={showConfirmPassword ? "text" : "password"} value={formData.confirmPassword} onChange={(e) => setFormData(p => ({ ...p, confirmPassword: e.target.value }))}
                  placeholder="Repite la contraseña" 
                  className={`bg-transparent border-white/20 text-white pr-10 ${formData.confirmPassword && !passwordsMatch ? 'border-red-500' : ''}`} required />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {formData.confirmPassword && !passwordsMatch && (
                <p className="text-red-400 text-xs mt-1">Las contraseñas no coinciden</p>
              )}
            </div>
            <Button type="button" onClick={handleNextStep} disabled={!canProceedStep1 || !passwordsMatch || checkingEmail} className="w-full btn-primary">
              {checkingEmail ? 'Verificando...' : 'Continuar'} <ChevronRight size={16} />
            </Button>
          </div>
        )}

        {/* Step 2: Personal */}
        {step === 2 && (
          <div className="space-y-4 animate-fadeIn">
            <h3 className="text-xl text-white font-medium flex items-center gap-2">
              <User size={20} className="text-[var(--primary)]" />
              Datos personales
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm block mb-2">Nombre *</label>
                <Input value={formData.first_name} onChange={(e) => setFormData(p => ({ ...p, first_name: e.target.value }))}
                  placeholder="Tu nombre" className="bg-transparent border-white/20 text-white" required />
              </div>
              <div>
                <label className="text-white/60 text-sm block mb-2">Apellidos *</label>
                <Input value={formData.last_name} onChange={(e) => setFormData(p => ({ ...p, last_name: e.target.value }))}
                  placeholder="Tus apellidos" className="bg-transparent border-white/20 text-white" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm block mb-2">Fecha de nacimiento *</label>
                <div className="relative">
                  <Input type="date" value={formData.birth_date} onChange={(e) => setFormData(p => ({ ...p, birth_date: e.target.value }))}
                    max={new Date().toISOString().split("T")[0]} min="1900-01-01"
                    className="bg-transparent border-white/20 text-white pr-10" required />
                  <Calendar
                    size={18}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-yellow-400 pointer-events-none"
                  />
                </div>
                {formData.birth_date && !isValidDate(formData.birth_date) && (
                  <p className="text-red-400 text-xs mt-1">
                    Introduce una fecha válida
                  </p>
                )}
              </div>
              <div>
                <label className="text-white/60 text-sm block mb-2">Teléfono</label>
                <Input type="tel" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="600 000 000" maxLength={11} className="bg-transparent border-white/20 text-white" />
                {formData.phone && !isValidPhone(formData.phone) && (
                  <p className="text-red-400 text-xs mt-1">
                    Introduce un teléfono válido (9 dígitos)
                  </p>
                )}
              </div>
            </div>
            <div className="relative">
              <Input
                value={cityQuery}
                onChange={(e) => {
                  setCityQuery(e.target.value);
                  setShowDropdown(true);
                  setFormData(p => ({ ...p, city: e.target.value }));
                }}
                placeholder="Busca tu ciudad"
                className="bg-transparent border-white/20 text-white"
              />
              {showDropdown && cityQuery && (
                <div className="absolute w-full bg-black border border-white/20 max-h-60 overflow-y-auto z-50 rounded-md">
                  {filteredCities.slice(0, 8).map(city => (
                    <div
                      key={city.id}
                      onClick={() => {
                        setCityQuery(city.label);
                        setFormData(p => ({ ...p, city: city.label }));
                        setShowDropdown(false);
                      }}
                      className="p-2 hover:bg-white/10 cursor-pointer text-white"
                    >
                      {city.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button type="button" onClick={() => setStep(1)} variant="outline" className="flex-1 btn-outline">Atrás</Button>
              <Button type="button" onClick={() => setStep(3)} disabled={!canProceedStep2} className="flex-1 btn-primary">
                Continuar <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preferences (was Step 4) */}
        {step === 3 && (
          <div className="space-y-4 animate-fadeIn">
            <h3 className="text-xl text-white font-medium flex items-center gap-2">
              <Heart size={20} className="text-[var(--primary)]" />
              Tus preferencias
            </h3>
            <p className="text-white/50 text-sm">Selecciona los géneros que más te interesan.</p>
            <div className="grid grid-cols-2 gap-3">
              {genres.map(genre => (
                <button key={genre.id} type="button" onClick={() => toggleGenre(genre.value)}
                  className={`p-4 text-left transition-all flex items-center justify-between ${
                    formData.favorite_genres.includes(genre.value)
                      ? 'bg-[var(--primary)]/20 border-2 border-[var(--primary)] text-white'
                      : 'bg-white/5 border-2 border-transparent text-white/70 hover:bg-white/10'
                  }`}>
                  <span>{genre.label}</span>
                  {formData.favorite_genres.includes(genre.value) && <Check size={18} className="text-[var(--primary)]" />}
                </button>
              ))}
            </div>
            <label className="flex items-start gap-3 p-4 bg-white/5 cursor-pointer">
              <input type="checkbox" checked={formData.accept_communications}
                onChange={(e) => setFormData(p => ({ ...p, accept_communications: e.target.checked }))} className="mt-1" />
              <span className="text-white/70 text-sm">Acepto recibir comunicaciones sobre eventos y ofertas.</span>
            </label>
            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
              <div className="flex justify-center py-2" data-testid="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(token) => { setRecaptchaToken(token || ''); setError(''); }}
                  onExpired={() => setRecaptchaToken('')}
                  theme="dark"
                />
              </div>
            )}
            <div className="flex gap-3">
              <Button type="button" onClick={() => setStep(2)} variant="outline" className="flex-1 btn-outline">Atrás</Button>
              <Button type="submit" disabled={loading} className="flex-1 btn-primary">
                {loading ? 'Registrando...' : 'Completar registro'}
              </Button>
            </div>
          </div>
        )}
      </form>

      <p className="text-center text-white/50 text-sm mt-6">
        ¿Ya tienes cuenta? <button onClick={onSwitchToLogin} className="text-[var(--primary)] hover:underline">Inicia sesión</button>
      </p>
    </div>
  );
};

// Login Form
const LoginForm = ({ onSuccess, onSwitchToRegister, onSwitchToForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/members/login`, { email, password });
      localStorage.setItem('memberToken', res.data.token);
      localStorage.setItem('memberData', JSON.stringify(res.data.member));
      // Dispatch event to update navbar immediately
      window.dispatchEvent(new Event('memberUpdate'));
      onSuccess(res.data.member);
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.headers?.["x-error-code"];
      if (status === 403 && code === "EMAIL_NOT_VERIFIED") {
        
        window.location.href = `/verificacion-enviada?email=${encodeURIComponent(email)}&reason=login`;
        return;
      }
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="p-4 bg-red-950/50 border border-red-500/30 text-red-400 text-sm">{error}</div>}
        <div>
          <label className="text-white/60 text-sm block mb-2">Email</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com" className="bg-transparent border-white/20 text-white" required />
        </div>
        <div>
          <label className="text-white/60 text-sm block mb-2">Contraseña</label>
          <div className="relative">
            <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña" className="bg-transparent border-white/20 text-white pr-10" required />
            <button type="button" onClick={() => setShowPassword(!showPassword)} 
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div className="text-right">
          <button type="button" onClick={onSwitchToForgotPassword} className="text-[var(--primary)] text-sm hover:underline">
            ¿Olvidaste tu contraseña?
          </button>
        </div>
        <Button type="submit" disabled={loading} className="w-full btn-primary">
          {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
        </Button>
      </form>
      <p className="text-center text-white/50 text-sm mt-6">
        ¿No tienes cuenta? <button onClick={onSwitchToRegister} className="text-[var(--primary)] hover:underline">Regístrate gratis</button>
      </p>
    </div>
  );
};

// Forgot Password Form
const ForgotPasswordForm = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0); // segundos restantes

  useEffect(() => {
    const tick = () => setCooldown(computeRemaining());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [email]);

  const COOLDOWN_SECONDS = 60;
  // clave por email para que no “bloquee” a otros correos
  const cooldownKey = (email) => `pwd_reset_cooldown_until:${(email || "").trim().toLowerCase()}`;

  const computeRemaining = () => {
    const key = cooldownKey(email);
    const until = parseInt(localStorage.getItem(key) || "0", 10);
    const diffMs = until - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    const remaining = computeRemaining();
    if (remaining > 0) {
      setCooldown(remaining);
      setError(`Espera ${remaining}s antes de volver a pedirlo.`);
      return;
    }
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(`${API}/members/password-reset-request`, { email });
      const until = Date.now() + COOLDOWN_SECONDS * 1000;
      localStorage.setItem(cooldownKey(email), String(until));
      setCooldown(COOLDOWN_SECONDS);
      setSuccess(true);
      toast.success("Si el email existe, te enviaremos un enlace de recuperación.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        const retryAfter = parseInt(err.response.headers?.["retry-after"] || "60", 10);
        setError(`Espera ${retryAfter}s antes de volver a pedirlo.`);
        return;
      }
      const retryAfter = parseInt(err.response.headers?.["retry-after"] || "60", 10);
      const until = Date.now() + retryAfter * 1000;
      localStorage.setItem(cooldownKey(email), String(until));
      setCooldown(retryAfter);
      setError(err.response?.data?.detail || 'Error al solicitar recuperación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <form onSubmit={handleRequestReset} className="space-y-6">
        <div className="text-center mb-6">
          <Lock size={48} className="text-[var(--primary)] mx-auto mb-4" />
          <h3 className="text-xl text-white font-medium">¿Olvidaste tu contraseña?</h3>
          <p className="text-white/60 text-sm mt-2">
            Introduce tu email y te enviaremos instrucciones para recuperarla
          </p>
        </div>
        
        {error && <div className="p-4 bg-red-950/50 border border-red-500/30 text-red-400 text-sm">{error}</div>}
        {success && (
          <div className="p-4 bg-green-950/50 border border-green-500/30 text-green-400 text-sm">
            Si el email existe, recibirás instrucciones para recuperar tu contraseña
          </div>
        )}
        
        <div>
          <label className="text-white/60 text-sm block mb-2">Email</label>
          <Input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com" 
            className="bg-transparent border-white/20 text-white" 
            required 
          />
        </div>
        
        <Button type="submit" disabled={loading || cooldown > 0} className="w-full btn-primary">
          {loading ? 'Enviando...' : cooldown > 0 ? `Espera ${cooldown}s` : 'Recuperar contraseña'}
        </Button>
      </form>
      <p className="text-center text-white/50 text-sm mt-6">
        <button onClick={onSwitchToLogin} className="text-[var(--primary)] hover:underline">
          Volver a iniciar sesión
        </button>
      </p>
    </div>
  );
};

// Member Dashboard Component
const MemberDashboard = ({ member: initialMember, onLogout }) => {
  const [member, setMember] = useState(initialMember);
  const [favoriteEvents, setFavoriteEvents] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [activeTab, setActiveTab] = useState('points');
  const [referralCode, setReferralCode] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', phone: '' });

  const benefits = member.benefits || { level: 'bronce', points: 0 };
  const levelConfig = LEVEL_CONFIG[benefits.level] || LEVEL_CONFIG.bronce;
  const LevelIcon = levelConfig.icon;

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('memberToken');
      try {
        // Fetch fresh member data
        const memberRes = await axios.get(`${API}/members/me?token=${token}`);
        setMember(memberRes.data);
        localStorage.setItem('memberData', JSON.stringify(memberRes.data));
        
        // Fetch favorites
        const favRes = await axios.get(`${API}/members/favorites/events?token=${token}`);
        setFavoriteEvents(favRes.data);
        
        // Fetch points history
        const historyRes = await axios.get(`${API}/members/points/history?token=${token}`);
        setPointsHistory(historyRes.data.history || []);
        
        // Fetch rewards
        const rewardsRes = await axios.get(`${API}/members/rewards?token=${token}`);
        setRewards(rewardsRes.data.rewards || []);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handler = async () => {
      const token = localStorage.getItem('memberToken');
      if (!token) return;
      try {
        const memberRes = await axios.get(`${API}/members/me?token=${token}`);
        setMember(memberRes.data);
        localStorage.setItem('memberData', JSON.stringify(memberRes.data));
      } catch {}
    };

    window.addEventListener('memberUpdate', handler);
    return () => window.removeEventListener('memberUpdate', handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('memberData');
    // Dispatch event to update navbar immediately
    window.dispatchEvent(new Event('memberUpdate'));
    onLogout();
  };

  const handleUnsubscribeNewsletter = async () => {
    if (!window.confirm('¿Estás seguro de que quieres darte de baja de la newsletter? Dejarás de recibir comunicaciones sobre eventos y ofertas.')) return;
    
    const token = localStorage.getItem('memberToken');
    try {
      await axios.post(`${API}/members/unsubscribe-newsletter?token=${token}`);
      toast.success('Te has dado de baja de la newsletter');
      // Update local member data
      const updatedMember = { ...member, accept_communications: false };
      setMember(updatedMember);
      localStorage.setItem('memberData', JSON.stringify(updatedMember));
    } catch (err) {
      toast.error('Error al procesar la solicitud');
    }
  };

  const copyReferralCode = () => {
    const code = member.referral_code || `REF-${member.member_number?.split('-')[2]}`;
    navigator.clipboard.writeText(code);
    toast.success('¡Código copiado! Compártelo con tus amigos');
  };

  const applyReferralCode = async () => {
    if (!referralCode.trim()) return;
    const token = localStorage.getItem('memberToken');
    try {
      const res = await axios.post(`${API}/members/referral/apply?token=${token}&referral_code=${referralCode}`);
      toast.success(res.data.message);
      // Refresh member data
      const memberRes = await axios.get(`${API}/members/me?token=${token}`);
      setMember(memberRes.data);
      setReferralCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código no válido');
    }
  };

  const redeemReward = async (rewardId) => {
    const token = localStorage.getItem('memberToken');
    try {
      const res = await axios.post(`${API}/members/rewards/redeem/${rewardId}?token=${token}`);
      toast.success(res.data.message);
      // Refresh data
      const memberRes = await axios.get(`${API}/members/me?token=${token}`);
      setMember(memberRes.data);
      const rewardsRes = await axios.get(`${API}/members/rewards?token=${token}`);
      setRewards(rewardsRes.data.rewards || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al canjear');
    }
  };

  const saveProfile = async () => {
    const token = localStorage.getItem('memberToken');
    try {
      const res = await axios.put(`${API}/members/me?token=${token}`, editForm);
      setMember(res.data);
      localStorage.setItem('memberData', JSON.stringify(res.data));
      setIsEditing(false);
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  // Calculate progress to next level
  const currentPoints = benefits.points || 0;
  const nextLevel = benefits.level === 'bronce' ? 'plata' : benefits.level === 'plata' ? 'oro' : null;
  const nextLevelMin = nextLevel ? LEVEL_CONFIG[nextLevel].min : currentPoints;
  const currentLevelMin = LEVEL_CONFIG[benefits.level]?.min || 0;
  const progressPercent = nextLevel 
    ? Math.min(100, ((currentPoints - currentLevelMin) / (nextLevelMin - currentLevelMin)) * 100)
    : 100;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Member Header with Level */}
      <div className="bg-gradient-to-r from-[var(--primary)]/20 to-transparent border border-[var(--primary)]/30 p-6 mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${levelConfig.color}20`, border: `2px solid ${levelConfig.color}` }}>
              <LevelIcon size={28} style={{ color: levelConfig.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: `${levelConfig.color}30`, color: levelConfig.color }}>
                  NIVEL {levelConfig.label.toUpperCase()}
                </span>
              </div>
              <h2 className="text-2xl text-white font-['Playfair_Display'] mt-1">{member.first_name} {member.last_name}</h2>
              <p className="text-white/50 text-sm">Nº Socio: {member.member_number}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold" style={{ color: levelConfig.color }}>{currentPoints}</p>
            <p className="text-white/50 text-xs uppercase">Puntos</p>
            {nextLevel && (
              <p className="text-white/30 text-xs mt-1">{LEVEL_CONFIG[nextLevel].min - currentPoints} pts para {nextLevel}</p>
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        {nextLevel && (
          <div className="mt-4">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-500" style={{ width: `${progressPercent}%`, backgroundColor: levelConfig.color }} />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 mb-8 overflow-x-auto">
        {[
          { id: 'points', label: 'Puntos', icon: Trophy },
          { id: 'rewards', label: 'Recompensas', icon: Gift },
          { id: 'favorites', label: 'Favoritos', icon: Heart },
          { id: 'referral', label: 'Invitar', icon: Users },
          { id: 'profile', label: 'Perfil', icon: User },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-4 text-sm whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'text-[var(--primary)] border-b-2 border-[var(--primary)]' : 'text-white/50 hover:text-white'
            }`}>
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'points' && (
        <div className="animate-fadeIn space-y-6">
          {/* Points earning info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { action: 'Añadir favorito', points: '+5', icon: Heart },
              { action: 'Compartir evento', points: '+5', icon: Share2 },
              { action: 'Invitar amigo', points: '+10', icon: Users },
              { action: 'Comprar entrada', points: '+50', icon: Ticket },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 p-4 text-center">
                <item.icon size={24} className="text-[var(--primary)] mx-auto mb-2" />
                <p className="text-white text-sm font-medium">{item.action}</p>
                <p className="text-[var(--primary)] font-bold">{item.points}</p>
              </div>
            ))}
          </div>

          {/* History */}
          <div className="bg-white/5 border border-white/10 p-6">
            <h3 className="text-lg text-white mb-4 flex items-center gap-2">
              <History size={18} className="text-[var(--primary)]" />
              Historial de puntos
            </h3>
            {pointsHistory.length === 0 ? (
              <p className="text-white/50 text-center py-4">Aún no tienes movimientos</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {pointsHistory.slice().reverse().map((entry, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                    <div>
                      <p className="text-white text-sm">{entry.description}</p>
                      <p className="text-white/40 text-xs">{new Date(entry.timestamp).toLocaleDateString('es-ES')}</p>
                    </div>
                    <span className={`font-bold ${entry.points > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {entry.points > 0 ? '+' : ''}{entry.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'rewards' && (
        <div className="animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rewards.map(reward => (
              <div key={reward.id} className={`bg-white/5 border p-6 ${reward.available ? 'border-[var(--primary)]/30' : 'border-white/10 opacity-60'}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-white font-medium">{reward.name}</h4>
                    <p className="text-white/50 text-sm">{reward.description}</p>
                  </div>
                  <span className="text-[var(--primary)] font-bold">{reward.points_cost} pts</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs px-2 py-1 bg-white/10 text-white/60">
                    Nivel mínimo: {LEVEL_CONFIG[reward.min_level]?.label}
                  </span>
                  <Button size="sm" disabled={!reward.available} onClick={() => redeemReward(reward.id)}
                    className={reward.available ? 'btn-primary' : ''}>
                    {reward.available ? 'Canjear' : 'No disponible'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'favorites' && (
        <div className="animate-fadeIn">
          <h3 className="text-xl text-white mb-6 flex items-center gap-2">
            <Heart size={20} className="text-[var(--primary)]" />
            Mis eventos favoritos
          </h3>
          {favoriteEvents.length === 0 ? (
            <div className="text-center py-12 bg-white/5 border border-white/10">
              <Heart size={48} className="text-white/20 mx-auto mb-4" />
              <p className="text-white/50 mb-4">Aún no tienes eventos favoritos</p>
              <Link to="/catalogo" className="btn-primary inline-flex items-center gap-2">
                Explorar eventos <ChevronRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {favoriteEvents.map(event => (
                <Link key={event.id} to={`/evento/${event.id}`}
                  className="flex gap-4 p-4 bg-white/5 border border-white/10 hover:border-[var(--primary)]/30 transition-colors">
                  <img src={event.image_url} alt={event.title} className="w-24 h-24 object-cover" />
                  <div>
                    <h4 className="text-white font-medium">{event.title}</h4>
                    <p className="text-white/50 text-sm">{event.venue}</p>
                    <p className="text-[var(--primary)] text-sm mt-2">
                      {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'referral' && (
        <div className="animate-fadeIn space-y-6">
          {/* Your referral code */}
          <div className="bg-gradient-to-r from-[var(--primary)]/10 to-transparent border border-[var(--primary)]/30 p-6">
            <h3 className="text-lg text-white mb-2">Tu código de referido</h3>
            <p className="text-white/50 text-sm mb-4">Comparte tu código y gana 200 puntos por cada amigo que se registre</p>
            <div className="flex gap-3">
              <div className="flex-1 bg-black/30 border border-white/20 p-3 text-center">
                <span className="text-xl font-mono text-[var(--primary)]">
                  {member.referral_code || `REF-${member.member_number?.split('-')[2] || '00000'}`}
                </span>
              </div>
              <Button onClick={copyReferralCode} className="btn-primary">
                <Copy size={18} />
              </Button>
            </div>
          </div>

          {/* Apply referral code */}
          {!member.referred_by && (
            <div className="bg-white/5 border border-white/10 p-6">
              <h3 className="text-lg text-white mb-2">¿Tienes un código de referido?</h3>
              <p className="text-white/50 text-sm mb-4">Introdúcelo y gana 200 puntos extra</p>
              <div className="flex gap-3">
                <Input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="REF-XXXXX" className="bg-transparent border-white/20 text-white font-mono" />
                <Button onClick={applyReferralCode} className="btn-primary">Aplicar</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="animate-fadeIn space-y-6">
          <div className="bg-white/5 border border-white/10 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg text-white flex items-center gap-2">
                <User size={18} className="text-[var(--primary)]" />
                Datos personales
              </h3>
              {!isEditing && (
                <Button size="sm" variant="outline" onClick={() => {
                  setEditForm({ first_name: member.first_name, last_name: member.last_name, phone: member.phone || '' });
                  setIsEditing(true);
                }} className="btn-outline">Editar</Button>
              )}
            </div>
            
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/60 text-sm block mb-2">Nombre</label>
                    <Input value={editForm.first_name} onChange={(e) => setEditForm(p => ({ ...p, first_name: e.target.value }))}
                      className="bg-transparent border-white/20 text-white" />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm block mb-2">Apellidos</label>
                    <Input value={editForm.last_name} onChange={(e) => setEditForm(p => ({ ...p, last_name: e.target.value }))}
                      className="bg-transparent border-white/20 text-white" />
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-sm block mb-2">Teléfono</label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))}
                    className="bg-transparent border-white/20 text-white" />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setIsEditing(false)} className="btn-outline">Cancelar</Button>
                  <Button onClick={saveProfile} className="btn-primary">Guardar</Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-white/40">Nombre</p>
                  <p className="text-white">{member.first_name} {member.last_name}</p>
                </div>
                <div>
                  <p className="text-white/40">Email</p>
                  <p className="text-white">{member.email}</p>
                </div>
                <div>
                  <p className="text-white/40">Teléfono</p>
                  <p className="text-white">{member.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-white/40">Miembro desde</p>
                  <p className="text-white">{new Date(member.member_since).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' })}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            {member.accept_communications !== false && (
              <Button onClick={handleUnsubscribeNewsletter} variant="outline" className="btn-outline flex items-center gap-2 text-white/50 hover:text-red-400 hover:border-red-400/30">
                <BellOff size={16} />
                Baja Newsletter
              </Button>
            )}
            <Button onClick={handleLogout} variant="outline" className="btn-outline flex items-center gap-2">
              <LogOut size={16} />
              Cerrar sesión
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper to get initial member from localStorage
const getInitialMemberState = () => {
  try {
    const token = localStorage.getItem('memberToken');
    const memberData = localStorage.getItem('memberData');
    if (token && memberData) {
      return { member: JSON.parse(memberData), view: 'dashboard' };
    }
  } catch {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('memberData');
  }
  return { member: null, view: 'login' };
};

// Main Page Component
export const MemberArea = () => {
  const initialState = getInitialMemberState();
  const [view, setView] = useState(initialState.view);
  const [member, setMember] = useState(initialState.member);

  const handleLoginSuccess = (memberData) => {
    setMember(memberData);
    setView('dashboard');
  };

  const handleRegisterSuccess = (memberData) => {
    setMember(memberData);
    setView('dashboard');
  };

  const handleLogout = () => {
    setMember(null);
    setView('login');
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] pt-32 pb-20">
      <div className="section-container">
        {/* Header */}
        {view !== 'dashboard' && (
          <div className="text-center mb-12">
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-4">
              my<span className="text-[var(--primary)]">EventCinema</span>
            </h1>
            <p className="text-white/60 max-w-xl mx-auto">
              Únete al club y acumula puntos canjeables por entradas gratis, eventos exclusivos y regalos.
            </p>

            {/* Benefits Preview */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-8">
              <div className="p-4 bg-white/5 border border-white/10">
                <Trophy size={24} className="text-[var(--primary)] mx-auto mb-2" />
                <p className="text-white text-sm font-medium">100 Puntos</p>
                <p className="text-white/40 text-xs">de bienvenida</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10">
                <Gift size={24} className="text-[var(--primary)] mx-auto mb-2" />
                <p className="text-white text-sm font-medium">Recompensas</p>
                <p className="text-white/40 text-xs">entradas y regalos</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10">
                <Star size={24} className="text-[var(--primary)] mx-auto mb-2" />
                <p className="text-white text-sm font-medium">3 Niveles</p>
                <p className="text-white/40 text-xs">Bronce, Plata, Oro</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {view === 'login' && (
          <LoginForm 
            onSuccess={handleLoginSuccess} 
            onSwitchToRegister={() => setView('register')} 
            onSwitchToForgotPassword={() => setView('forgotPassword')}
          />
        )}
        {view === 'register' && (
          <RegisterForm onSuccess={handleRegisterSuccess} onSwitchToLogin={() => setView('login')} />
        )}
        {view === 'forgotPassword' && (
          <ForgotPasswordForm onSwitchToLogin={() => setView('login')} />
        )}
        {view === 'dashboard' && member && (
          <MemberDashboard member={member} onLogout={handleLogout} />
        )}
      </div>
    </div>
  );
};

export default MemberArea;
